/* eslint-disable react/jsx-no-duplicate-props */
import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useDispatch } from 'react-redux'
import { useSelector } from 'hooks/redux'
import { getInputSignalSwitches } from 'selectors/feature/inputs'
import { sort, map, compose, isEmpty } from 'ramda'
import strings from 'utils/strings'
import {
  ANALOG_INPUT,
  COMPLEX,
  DIGITAL_INPUT,
  QUERY_INPUT_TYPES,
  TIMER,
} from 'utils/constants'
import { isNativeInput, isDigitalInput } from 'utils/types/features'
import { RESTRICTED, LIMITED } from 'selectors/feature/utils'
import conditionSchema from 'schemas/validation/feature/conditionSchema'
import { schemaProp } from 'schemas/validation'
import InputSwitchList from 'components/feature/NavigatorPanes/editors/base/InputSwitchList'
import {
  InputConnectorFinder,
  AnalogInputHelper,
} from 'components/feature/ConnectorFinderMenu'
import { makeGetNewLogicNodeIndex } from 'selectors/feature/logicNodes'
import { themeColor } from 'utils/styles'
import { addLogicNodeToInput } from 'actions/feature/inputs'
import {
  StyledSelectMenu,
  OptionRow,
  OptionLabel,
  SwitchCustomNamePill,
  PillText,
} from './InputSignalSelector.style'

const rule = schemaProp(conditionSchema)
const { CARRY, CAN_INPUT } = QUERY_INPUT_TYPES

const refGroundPinMap = {
  'Analog Input 01': {
    desc: 'Reference Pin 01: X1-6 (Ground)',
  },
  'Analog Input 02': {
    desc: 'Reference Pin 02: X1-13 (Ground)',
  },
  'Analog Input 03': {
    desc: 'Reference Pin 03: X1-25 (Ground)',
  },
  'Analog Input 04': {
    desc: 'Reference Pin 04: X1-8 (Ground)',
  },
}

const InputSignalSelector = ({
  selectedType,
  onInputChange,
  inputOptions,
  selectedInputUri,
  isOutputBoolean,
  isNativeOutput,
  isSixteenBitCanOutput,
  pinSet,
  featureModuleName,
  featureType,
  showLabel,
  isTimerActivateCondition,
  inputUri,
  interfacePins,
  selectedInputDescription,
  restrictions,
  uri,
}) => {
  const currentSignalSwitches = useSelector(getInputSignalSwitches, {
    signal: inputUri,
  })

  const canAddLogicSet = selectedType === CARRY && restrictions !== LIMITED
  const canAddTimer = selectedType === CARRY && restrictions !== RESTRICTED

  const defaults = [
    ...(canAddLogicSet
      ? [
          {
            key: 'create_logic_set',
            title: <CreateNewLogicSet uri={uri} />,
          },
        ]
      : []),
    ...(canAddTimer
      ? [
          {
            key: 'create_timer',
            title: <CreateNewTimer uri={uri} />,
          },
        ]
      : []),
  ]

  const toMenuItem = (option) => ({
    key: option.uri,
    value: option.uri,
    title:
      selectedType === ANALOG_INPUT
        ? `${option.description} (${strings.analog_input_feedback})`
        : option.description,
    customDescription: option.customDescription || null,
  })

  const menuOptions = compose(
    map(toMenuItem),
    sort((a, b) =>
      a.description.localeCompare(b.description, undefined, { numeric: true })
    )
  )(inputOptions)

  const selectLabel = () => {
    if (
      isOutputBoolean === false &&
      isNativeOutput &&
      isNativeInput(selectedType)
    )
      return strings.non_boolean_inputs
    if (isNativeOutput && isNativeInput(selectedType))
      return strings.boolean_inputs
    if (isSixteenBitCanOutput && isNativeInput(selectedType))
      return strings.non_boolean_inputs
    if (isNativeOutput && isDigitalInput(selectedType)) return strings.input_pin
    return (
      {
        [CAN_INPUT]: strings.can,
        [CARRY]: strings.carry_from,
      }[selectedType] || strings.input
    )
  }
  const hasSwitches = !isEmpty(currentSignalSwitches)
  const showConnectorFinder =
    selectedType === DIGITAL_INPUT && selectedInputUri && interfacePins

  const showContextualHelpForAnalogInput =
    selectedType === ANALOG_INPUT && selectedInputUri

  // Generate key from inputOptions URIs, descriptions AND customDescriptions to force Select
  // remount when options change. Fixes Ant Design 3.x Select not updating dropdown when
  // options prop changes. Including customDescription ensures remount when a custom name is
  // set or cleared.
  const optionsKey = inputOptions
    .map(
      (opt) => `${opt.uri}:${opt.description}:${opt.customDescription || ''}`
    )
    .join(',')

  return (
    <>
      <StyledSelectMenu
        key={optionsKey}
        showSearch
        name={strings.select}
        placeholder={strings.select}
        value={selectedInputUri}
        onChange={onInputChange}
        options={[...defaults, ...menuOptions]}
        filterOption={(inputValue, option) => {
          const found = menuOptions.find((o) => o.value === option.props.value)
          if (!found) return true // keep default actions (create logic set, create timer) always visible
          const searchTerm = inputValue.toUpperCase()
          const titleMatch =
            (found.title || '').toUpperCase().indexOf(searchTerm) !== -1
          const customDescMatch =
            (found.customDescription || '')
              .toUpperCase()
              .indexOf(searchTerm) !== -1
          return titleMatch || customDescMatch
        }}
        optionLabelProp="title"
        mask={(title, optionValue) => {
          const found = menuOptions.find((o) => o.value === optionValue)
          if (!found?.customDescription) return title
          return (
            <OptionRow>
              <OptionLabel>{title}</OptionLabel>
              <SwitchCustomNamePill>
                <PillText>{found.customDescription}</PillText>
              </SwitchCustomNamePill>
            </OptionRow>
          )
        }}
        className="form-field"
        label={showLabel ? selectLabel() : null}
        rules={rule('signal', {
          isTimerActivateCondition,
          subType: selectedType,
          pinSet,
          featureModuleName,
          featureType,
        })}
        data-testid="input-signal-select"
      />
      {showContextualHelpForAnalogInput && (
        <AnalogInputHelper
          selectedPin={`${
            inputOptions.find((option) => option.uri === selectedInputUri)
              ?.description
          } (${strings.analog_input_feedback})`}
          referencePin={refGroundPinMap[selectedInputDescription]?.desc}
        />
      )}
      {showConnectorFinder && (
        <InputConnectorFinder
          inputUri={inputUri}
          interfacePins={interfacePins}
        />
      )}
      {hasSwitches && (
        <InputSwitchList
          currentInputOrOutputSwitches={currentSignalSwitches}
          marginTop={'0px'}
        />
      )}
    </>
  )
}

InputSignalSelector.propTypes = {
  selectedType: PropTypes.string,
  onInputChange: PropTypes.func,
  inputOptions: PropTypes.array,
  selectedInputUri: PropTypes.string,
  isOutputBoolean: PropTypes.bool,
  isNativeOutput: PropTypes.bool,
  isSixteenBitCanOutput: PropTypes.bool,
  switchNumber: PropTypes.string,
  pinSet: PropTypes.object,
  featureModuleName: PropTypes.string,
  featureType: PropTypes.string,
  switchDescription: PropTypes.string,
  showLabel: PropTypes.bool,
  currentSignalSwitches: PropTypes.object,
  /** needed for validation, if it is the activate condition on a timer */
  isTimerActivateCondition: PropTypes.bool,
  inputUri: PropTypes.string,
  selectedInputDescription: PropTypes.string,
  interfacePins: PropTypes.array,
  uri: PropTypes.string,
  restrictions: PropTypes.string,
}

export default InputSignalSelector

const StyledDropdownLink = styled.div`
  color: ${themeColor('bluePrimary')};
`

const CreateNewLogicSet = ({ uri }) => {
  const dispatch = useDispatch()
  const getNewLogicSetIndex = makeGetNewLogicNodeIndex(COMPLEX)
  const nextIndex = useSelector(getNewLogicSetIndex)

  const handleClick = () => {
    dispatch(addLogicNodeToInput(COMPLEX, nextIndex, uri))
  }

  return (
    <StyledDropdownLink onClick={handleClick}>
      + {strings.create_logic_set}
    </StyledDropdownLink>
  )
}

CreateNewLogicSet.propTypes = {
  fnId: PropTypes.string,
  uri: PropTypes.string,
}

const CreateNewTimer = ({ uri }) => {
  const dispatch = useDispatch()
  const getNewTimerIndex = makeGetNewLogicNodeIndex(TIMER)

  const nextIndex = useSelector(getNewTimerIndex)

  const handleClick = () => {
    dispatch(addLogicNodeToInput(TIMER, nextIndex, uri))
  }

  return (
    <StyledDropdownLink onClick={handleClick}>
      + {strings.create_timer}
    </StyledDropdownLink>
  )
}

CreateNewTimer.propTypes = {
  fnId: PropTypes.string,
  uri: PropTypes.string,
}
